import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notasFiscaisApi } from '../../api/endpoints'
import { Badge } from '../../components/ui/Badge'
import { ProgressBar } from '../../components/ui/Card'
import { Select } from '../../components/ui/Input'
import { LoadingState } from '../../components/ui/EmptyState'
import { useToast } from '../../components/ui/Toast'
import { formatMoney, cn } from '../../lib/utils'
import { BloqueioPremium } from './BloqueioPremium'
import { usePermiteNotas } from './usePermiteNotas'
import { ROTULO_ESSENCIAL, VARIANTE_ESSENCIAL } from './rotulosItens'
import type { EssencialOpcional, ItemPesado } from '../../types'


/** Variação de preço do item contra o que o usuário costuma pagar (positivo = mais caro). */
function textoComparacao(it: ItemPesado) {
  const c = it.comparacao
  if (!c) return { texto: it.motivoSemComparacao ?? 'Poucas compras para comparar', cor: 'text-muted' }
  const v = c.variacaoPercentual
  const base = c.base === 'marca' ? 'mesma marca' : 'grupo todo'
  if (Math.abs(v) < 2) return { texto: `Dentro do normal (${base}, usual ${formatMoney(c.precoUsual)})`, cor: 'text-muted' }
  return v > 0
    ? { texto: `${v.toFixed(0)}% acima do que você paga (${base}, usual ${formatMoney(c.precoUsual)})`, cor: 'text-expense' }
    : { texto: `${Math.abs(v).toFixed(0)}% abaixo do que você paga (${base}, usual ${formatMoney(c.precoUsual)})`, cor: 'text-income' }
}

/** Insights de uma nota: essencial x opcional, categorias, o que mais pesou e classificação item a item. */
export function InsightsNota({ notaId, somenteLeitura }: { notaId: string; somenteLeitura?: boolean }) {
  const qc = useQueryClient()
  const toast = useToast()
  const { permite } = usePermiteNotas()
  const { data: ins, isLoading, isError, error } = useQuery({ queryKey: ['nota-insights', notaId], queryFn: () => notasFiscaisApi.insights(notaId), enabled: permite })
  const { data: catalogo } = useQuery({ queryKey: ['itens-catalogo'], queryFn: () => notasFiscaisApi.catalogoItens(), staleTime: Infinity, enabled: permite })

  const classificar = useMutation({
    mutationFn: (v: { itemId: string; essencial: EssencialOpcional }) => notasFiscaisApi.definirEssencialItem(v.itemId, v.essencial),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['nota-insights', notaId] }); toast.success('Item classificado só nesta nota.') },
    onError: (e: Error) => toast.error(e.message),
  })

  if (!permite) return <BloqueioPremium recurso="Os insights da nota" />
  if (isLoading) return <LoadingState message="Calculando os insights..." />
  if (isError || !ins) return <p className="rounded bg-surface2 px-3 py-3 text-13 text-muted">{(error as Error | null)?.message ?? 'Não consegui montar os insights desta nota.'}</p>

  const nomeCategoria = (c: string) => catalogo?.categorias.find((x) => x.codigo === c)?.nome ?? c
  const classificados = ins.essencialValor + ins.opcionalValor

  return (
    <div className="space-y-5">
      <div>
        <p className="text-13 text-white">{ins.estabelecimento} · {ins.itens} itens</p>
        <p className="text-xl font-semibold text-white">{formatMoney(ins.total)}</p>
      </div>

      <section className="space-y-2">
        <p className="text-13 font-medium text-white">Essencial e opcional</p>
        {classificados > 0 ? (
          <>
            <div className="flex h-3 overflow-hidden rounded-full bg-surface3" role="img" aria-label={`${ins.essencialPercentual.toFixed(0)}% essencial, ${ins.opcionalPercentual.toFixed(0)}% opcional`}>
              <div className="bg-income" style={{ width: `${ins.essencialPercentual}%` }} />
              <div className="bg-warning" style={{ width: `${ins.opcionalPercentual}%` }} />
            </div>
            <div className="flex flex-wrap justify-between gap-2 text-13">
              <span className="text-income"><strong>{ins.essencialPercentual.toFixed(0)}%</strong> essencial · {formatMoney(ins.essencialValor)}</span>
              <span className="text-warning"><strong>{ins.opcionalPercentual.toFixed(0)}%</strong> opcional · {formatMoney(ins.opcionalValor)}</span>
            </div>
          </>
        ) : (
          <p className="text-13 text-muted">Nenhum item classificado ainda.</p>
        )}
        {ins.semClassificacaoValor > 0 && (
          <p className="text-2xs text-muted">Itens sem classificação ({formatMoney(ins.semClassificacaoValor)}) ficam fora da porcentagem.</p>
        )}
      </section>

      <section className="space-y-2">
        <p className="text-13 font-medium text-white">Composição por categoria</p>
        {ins.categorias.map((c) => (
          <div key={c.categoria}>
            <div className="flex justify-between text-13"><span className="text-white">{nomeCategoria(c.categoria)}</span><span className="text-muted"><strong className="text-white">{c.percentual.toFixed(0)}%</strong> · {formatMoney(c.valor)}</span></div>
            <ProgressBar value={c.percentual} tone="info" className="mt-1" />
          </div>
        ))}
      </section>

      <section className="space-y-2">
        <p className="text-13 font-medium text-white">O que mais pesou</p>
        {ins.maisPesaram.map((it) => {
          const cmp = textoComparacao(it)
          return (
            <div key={it.itemId} className="rounded border border-border/70 p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-13 font-medium text-white">{it.descricao}</p>
                  <p className="text-2xs text-muted">{[it.grupoNome, it.marca, nomeCategoria(it.categoria)].filter(Boolean).join(' · ')}</p>
                </div>
                <p className="text-13 text-white"><strong>{formatMoney(it.valor)}</strong> <span className="text-muted">· {it.percentual.toFixed(0)}%</span></p>
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <p className={cn('text-2xs', cmp.cor)}>{cmp.texto}</p>
                {somenteLeitura ? (
                  <Badge variant={VARIANTE_ESSENCIAL[it.essencial]}>{ROTULO_ESSENCIAL[it.essencial]}</Badge>
                ) : (
                  <div className="w-44">
                    <Select
                      aria-label={`Classificar ${it.descricao} só nesta nota`}
                      value={it.essencialPorExcecao ? it.essencial : 0}
                      disabled={classificar.isPending}
                      onChange={(e) => classificar.mutate({ itemId: it.itemId, essencial: Number(e.target.value) as EssencialOpcional })}
                    >
                      <option value={0}>{it.essencial === 0 ? 'Sem classificação' : `Padrão: ${ROTULO_ESSENCIAL[it.essencial].toLowerCase()}`}</option>
                      <option value={1}>Essencial nesta nota</option>
                      <option value={2}>Opcional nesta nota</option>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          )
        })}
        <p className="text-2xs text-muted">O padrão vem do grupo do item. Trocar aqui vale só para esta nota, por exemplo um chocolate opcional que virou essencial numa festa. A comparação de preço aparece com 3 ou mais compras na base.</p>
      </section>
    </div>
  )
}
